import { GenericImageGalleryProps } from "@command-center-app/index";
import { colorPalette } from "@command-center-app/utils/colorPalette";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { Box, Checkbox, Grid } from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Radio from "@mui/material/Radio";
import React, { useEffect, useMemo, useRef, useState } from "react";
import RadioGroup from "@mui/material/RadioGroup";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import {
  handleDownload,
  handleScroll,
  handleScrollLeft,
} from "../../utils/index";
import { imageListStyle } from "./styles/commonStyles";

import DescriptionIcon from "@mui/icons-material/Description";
import Typography from "@mui/material/Typography";

const iconMap: any = {
  eps: { label: "EPS", Icon: DescriptionIcon },
  ai: { label: "AI", Icon: DescriptionIcon },
};

function FileIcon({ ext }: any) {
  const config = iconMap[ext] || iconMap["eps"];
  const Icon = config.Icon;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        border: "1px solid #e0e0e0",
        bgcolor: "#fafafa",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Icon sx={{ fontSize: 56 }} />

      <Typography
        variant="caption"
        sx={{
          position: "absolute",
          bottom: 4,
          fontWeight: 700,
          fontSize: 14,
          letterSpacing: 0.5,
        }}
      >
        EPS
      </Typography>
    </Box>
  );
}

const GenericImageListing: React.FC<GenericImageGalleryProps> = ({
  handleSelectItems,
  imagelist,
  showArrow = false,
  showActions = false,
  showCheckbox = true,
  showFooterAction = false,
  footerActionLable,
  maxImage = 0,
  uploadedImgList = [],
  handleDeleteImage,
  showGridView = false,
}) => {
  const [hover, setHover] = useState<boolean>(true);
  const scrollRef = useRef<any>(null);

  const imgList = useMemo(() => {
    if (uploadedImgList?.length > 0) {
      return [...imagelist, ...uploadedImgList];
    }

    return imagelist;
  }, [imagelist, uploadedImgList]);

  const defaultImg = useMemo(
    () => imgList.find((item: any) => item.isDefault),
    [imgList],
  );

  const [checkedIndex, setCheckedIndex] = useState<string | null>(
    defaultImg?.url ?? null,
  );
  const [checkedUrls, setCheckedUrls] = useState<string[]>([]);

  useEffect(() => {
    const lastUploadedImg = uploadedImgList.at(-1);
    if (lastUploadedImg?.isDefault) {
      setCheckedIndex(lastUploadedImg?.url);
      handleSelectItems?.(lastUploadedImg);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadedImgList]);

  const getImageHeight = (isGridDefault = false) => {
    if (isGridDefault || !showGridView) {
      return !showFooterAction ? "170px" : "132px";
    } else {
      return !showFooterAction ? "77px" : "62px";
    }
  };

  const getImageWidth = (isGridDefault = false) => {
    return isGridDefault || !showGridView ? 180 : 80;
  };

  const getCheckBoxCss = (isGridDefault = false) => {
    if (showGridView && !isGridDefault) {
      return {
        top: 0,
        right: -8,
      };
    }
    return {};
  };

  const renderImageItem = (i: any, isGridDefault = false) => (
    <Box
      key={i.url}
      sx={{
        ...imageListStyle.imageContainerStyle,
        position: "relative",
        flexShrink: 0,
        width: getImageWidth(isGridDefault),
        marginRight: 2,
      }}
    >
      <Box
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        sx={{ position: "relative" }}
      >
        <Box
          sx={{
            ...imageListStyle.actionContainer,
            ...getCheckBoxCss(isGridDefault),
          }}
        >
          {showCheckbox && (
            <Checkbox
              checked={checkedUrls.includes(i.url)}
              onChange={() => {
                const isSelected = checkedIndex === i.url;
                setCheckedIndex(isSelected ? null : i.url);
                setCheckedUrls((prev) =>
                  prev.includes(i.url)
                    ? prev.filter((url) => url !== i.url)
                    : [...prev, i.url],
                );
                handleSelectItems?.(i);
              }}
              sx={{ padding: 0, marginRight: 1 }}
            />
          )}
          {hover && showActions && (
            <>
              <a
                href={i.url}
                download
                target="_blank"
                rel="noreferrer"
                onClick={() =>
                  handleDownload(
                    i.url,
                    String(i.url).split("/").slice(-2, -1)[0].split(".")[0] +
                      "." +
                      String(i.url).split("/").slice(-1).toString(),
                  )
                }
                style={imageListStyle.actionIconStyle}
              >
                <SaveAltIcon sx={{ color: colorPalette.colorDarkGray }} />
              </a>

              <a
                style={imageListStyle.actionIconStyle}
                onClick={() => handleDeleteImage?.(i)}
              >
                <DeleteOutlineIcon sx={{ color: colorPalette.colorDarkGray }} />
              </a>
            </>
          )}
        </Box>
        {String(i.url).toLowerCase().endsWith(".pdf") ? (
          <Box
            sx={{
              ...imageListStyle.imageStyle,
              height: !showFooterAction ? "170px" : "132px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: colorPalette.colorOffWhite,
              border: "1px solid",
              borderColor: colorPalette.colorLightGrayishBlue,
              cursor: "pointer",
            }}
            onClick={() => window.open(i.url, "_blank")}
          >
            <PictureAsPdfIcon
              sx={{ fontSize: 60, color: colorPalette.error }}
            />
          </Box>
        ) : String(i.url)
            .toLowerCase()
            .match(/\.(mp4|webm|ogg|mov|avi)$/) ? (
          <video
            src={i.url}
            controls
            style={{
              ...imageListStyle.imageStyle,
              height: getImageHeight(isGridDefault),
              objectFit: "cover",
            }}
          />
        ) : String(i.url)
            .toLowerCase()
            .match(/\.(eps|ai)$/) ? (
          <Box
            sx={{ height: getImageHeight(isGridDefault), cursor: "pointer" }}
            onClick={() => {
              setCheckedUrls((prev) =>
                prev.includes(i.url)
                  ? prev.filter((url) => url !== i.url)
                  : [...prev, i.url],
              );
              handleSelectItems?.(i);
            }}
          >
            <FileIcon
              ext={
                String(i.url)
                  .toLowerCase()
                  .match(/\.(eps|ai)$/)?.[0]
                  .split(".")[1] || "default"
              }
            />
          </Box>
        ) : (
          <img
            src={i.url}
            alt="img"
            style={imageListStyle.imageStyle}
            height={getImageHeight(isGridDefault)}
          />
        )}
      </Box>
      {showFooterAction && (
        <RadioGroup row name="position" defaultValue="top">
          <FormControlLabel
            control={
              <Radio
                checked={checkedIndex === i.url}
                onClick={() => {
                  const isSelected = checkedIndex === i.url;
                  const newValue = isSelected ? null : i.url;
                  setCheckedIndex(newValue);
                  handleSelectItems?.(isSelected ? null : i);
                }}
              />
            }
            label={footerActionLable}
            sx={{
              ...imageListStyle.lableText,
              "& .MuiFormControlLabel-label": {
                fontWeight: 200,
                fontSize: "14px",
              },
            }}
          />
        </RadioGroup>
      )}
    </Box>
  );

  const renderRow = (row: any[]) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "",
        marginBottom: 2,
      }}
    >
      {row.map((item: any) => !item?.isDefault && renderImageItem(item))}
    </Box>
  );

  if (showGridView) {
    const totalImages = Math.min(imgList.length, maxImage);
    const half = Math.ceil(totalImages / 2);

    const firstRow = imgList.slice(0, half);
    const secondRow = imgList.slice(half, totalImages);

    return (
      <>
        {defaultImg && renderImageItem(defaultImg, true)}
        <Box>
          {renderRow(firstRow)}
          {renderRow(secondRow)}
        </Box>
      </>
    );
  }

  return (
    <Grid sx={{ display: "flex" }}>
      {showArrow && (
        <Box sx={imageListStyle.imageListContainer}>
          <IconButton
            disableRipple
            onClick={() => handleScrollLeft(scrollRef.current, 1000, 100, 200)}
          >
            <ChevronLeftIcon />
          </IconButton>
        </Box>
      )}
      <Box
        ref={scrollRef}
        sx={{ overflowX: "auto", display: "flex", position: "relative" }}
      >
        {imgList.map((i: any, index: number) => {
          if (maxImage > index) {
            return renderImageItem(i);
          }
        })}
      </Box>

      {showArrow && (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            disableRipple
            onClick={() => handleScroll(scrollRef.current, 1000, 100, 200)}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      )}
    </Grid>
  );
};

export default GenericImageListing;
